import { create } from "zustand";

export type TermDefinition = {
  term: string;
  definition: string;
};

type TermsByProject = Record<string, TermDefinition[]>;

interface TermsState {
  termsByProject: TermsByProject;
  getTerms: (projectId: string) => TermDefinition[];
  setTerms: (projectId: string, terms: TermDefinition[]) => void;
  addTerms: (projectId: string, newTerms: TermDefinition[]) => void;
  clearTerms: (projectId: string) => void;
  clearAll: () => void;
}

export const useTermsStore = create<TermsState>((set, get) => ({
  termsByProject: {},

  getTerms: (projectId: string) => get().termsByProject[projectId] ?? [],

  setTerms: (projectId: string, terms: TermDefinition[]) => {
    set((state) => ({
      termsByProject: {
        ...state.termsByProject,
        [projectId]: [...terms],
      },
    }));
  },

  addTerms: (projectId: string, newTerms: TermDefinition[]) => {
    if (!newTerms?.length) return;
    set((state) => {
      const existing = state.termsByProject[projectId] ?? [];
      // Create a map by lowercased term to avoid duplicates, prefer latest definition
      const termMap = new Map<string, TermDefinition>();
      for (const t of existing) {
        termMap.set(t.term.toLowerCase(), t);
      }
      for (const t of newTerms) {
        termMap.set(t.term.toLowerCase(), t);
      }

      return {
        termsByProject: {
          ...state.termsByProject,
          [projectId]: Array.from(termMap.values()),
        },
      };
    });
  },

  clearTerms: (projectId: string) => {
    set((state) => {
      if (!(projectId in state.termsByProject)) return state;
      const { [projectId]: _omit, ...rest } = state.termsByProject;
      return { termsByProject: rest };
    });
  },

  clearAll: () => set({ termsByProject: {} }),
}));
